/**
 * The NAMES of the app's icons - and nothing else. The drawings live in `src/frontend/icons.ts`.
 *
 * ## Why the names are split off, and split off into `shared/`
 *
 * Round 146 found the icons that most needed the round-145 gate sitting on the wrong side of it. The
 * gate scans `src/frontend`; the dashboard's attention strip gets its mark from the SERVER - a
 * `AttentionRow.icon` field that `services/dashboard.ts` filled with an emoji - so ten pictures that
 * render in the app were invisible to every check, for the ordinary reason that they are not written
 * where pictures are written. **A payload field is a call site the scanner cannot see.**
 *
 * The server is genuinely the right place to choose that one: it is what knows whether a row is a
 * held promise, a paid-placement offer or a stalled sweep, and the strip is a heterogeneous list
 * where the mark is how the owner scans it. What was wrong is only what the field HELD. So it holds
 * a NAME now, and a name both sides must agree on is precisely what `shared/` is for (see the root
 * CLAUDE.md: "anything a page and the renderer must agree on lives here, so the two cannot drift").
 *
 * ⚠ **This is WIRE DATA in the round-122 sense, not chrome**: an icon name is sent over the wire and
 * matched against the renderer's vocabulary, so it must never go through `t()` and must never be
 * translated. It is the same kind of string as a genre or a region code.
 *
 * ## ⚠ Why the DRAWINGS are deliberately NOT here
 *
 * `shared/` is imported by the published-page renderers, which are measured in bytes to first play,
 * and the root CLAUDE.md forbids `icons.ts` from ever crossing into them. A name union costs zero
 * bytes at runtime (types are erased); 101 SVG path bodies would cost ~20KB on a page whose whole
 * job is to be already-painted. Keeping the two files apart is what makes that boundary structural
 * rather than a rule someone has to remember - there is nothing in here to accidentally import.
 *
 * `tests/icons.test.ts` asserts the two agree in BOTH directions: a name with no drawing renders
 * nothing, and a drawing with no name cannot be asked for.
 *
 * ## ⚠ One name is deliberately ABSENT, and it is the set's sharpest rule
 *
 * There is no `handshake`. Round 145 shipped one, looked at it, and wrote it down as "still reading
 * as an abstract mark rather than the thing"; round 146 drew three more and rendered all four side by
 * side at 64/30/16px, and every one of them read as a knot at the size the app actually uses (1em).
 * The conclusion is not that the drawing needed another go: **an icon is a picture at a SIZE, and a
 * metaphor that needs more detail than a 24 grid at 1.75 stroke can hold is the wrong metaphor, not a
 * drawing problem.** Two clasped hands is exactly that metaphor - the reading depends on fingers.
 *
 * Its one call site (Blogs → "View relationships") uses `users` instead, which reads instantly and is
 * what the sentence beside it already says. That is allowed by the set's own naming rule - a name
 * says what the picture IS, not what one caller uses it for - so one drawing serving several call
 * sites is the normal case rather than a compromise.
 */
export const ICON_NAMES = [
	"warning",
	"info",
	"check",
	"check-circle",
	"ban",
	"close",
	"flag",
	"shield",
	"party",
	"trophy",
	"star",
	"heart",
	"thumbs-up",
	"hand",
	"thumbs-down",
	"question",
	"bell",
	"arrow-up-right",
	"arrow-down",
	"arrow-up",
	"arrow-back",
	"link",
	"external-link",
	"share",
	"compass",
	"search",
	"filter",
	"sort",
	"globe",
	"translate",
	"pin",
	"rocket",
	"plug",
	"gear",
	"home",
	"gauge",
	"grid",
	"list",
	"list-check",
	"menu",
	"dots",
	"chevron-down",
	"chevron-right",
	"play",
	"pause",
	"stop",
	"repeat",
	"shuffle",
	"mic",
	"headphones",
	"music-note",
	"music-notes",
	"disc",
	"volume",
	"pulse",
	"film",
	"clapper",
	"video-camera",
	"camera",
	"image",
	"scissors",
	"crop",
	"eye",
	"waves",
	"wave-line",
	"lungs",
	"fog",
	"sunrise",
	"bridge",
	"target",
	"sliders",
	"sparkles",
	"palette",
	"angle",
	"pencil",
	"write",
	"trash",
	"plus",
	"minus",
	"copy",
	"refresh",
	"download",
	"upload",
	"type",
	"flask",
	"bolt",
	"bulb",
	"clock",
	"stopwatch",
	"hourglass",
	"calendar",
	"sun",
	"moon",
	"folder",
	"file",
	"note",
	"journal",
	"book",
	"clipboard",
	"archive",
	"bookmark",
	"tag",
	"paperclip",
	"newspaper",
	"chart-bar",
	"chart-line",
	"mail",
	"mailbox",
	"postbox",
	"send",
	"inbox",
	"comment",
	"megaphone",
	"users",
	"user",
	"money",
	"credit-card",
	"gift",
	"recycle",
	"building",
	"phone",
	// The Devices card's fallback mark (2026-08-28). ⚠ There is deliberately no `tablet` beside it:
	// a tablet and a phone differ only in aspect ratio, and round 146's rule is that an icon is a
	// picture at a SIZE - two rounded rectangles are the same picture at 1em. A tablet session draws
	// `phone`, and the row's NAME ("Safari on iPadOS") is what carries the distinction.
	"laptop",
	"cloud",
	"robot",
	"save",
	"lock",
	"key",
	"brand-spotify",
	// The other three music services the artist finder searches (2026-08-07). ⚠ They arrived for the
	// same reason `brand-spotify` did: a surface that talks to a store had no mark for it, so the
	// omnibox's merged row could say "found on three services" only in words. A row whose whole job is
	// "which of these is you" is exactly where a mark does work a label cannot.
	"brand-apple-music",
	"brand-soundcloud",
	"brand-deezer",
	// The browsers a session can be signed in from (2026-08-28, `shared/userAgent.ts`). ⚠ **Edge and
	// Samsung Internet are deliberately absent and will stay that way**: Microsoft had its mark removed
	// from `simple-icons` on trademark grounds, and this file's standing rule is that a trademark drawn
	// by hand is wrong on its own terms. They get their NAME and the device's glyph - the parser is
	// built so a missing mark degrades on its own. ⚠ **Safari is absent for a different reason and is
	// NOT a gap to fill**: its official geometry is 11.7KB, four times every other brand mark here put
	// together, on a file that ships in the bundle round 279 cut to 183KB - and Safari's logo is a
	// compass, which this set already draws.
	"brand-chrome",
	"brand-firefox",
	"brand-opera",
	"brand-instagram",
	"brand-youtube",
	"brand-tiktok",
	"brand-facebook",
	"brand-threads",
	"brand-reddit",
	"brand-linkedin",
	"brand-pinterest",
	"brand-snapchat",
	"brand-whatsapp",
	"brand-telegram",
	"brand-discord",
	"brand-bluesky",
	"brand-x",
	// ⚠ **Google is here as an IDENTITY PROVIDER, not as a platform** (2026-08-09, owner: the sign-in
	// button should carry the mark). Every other entry above is somewhere a song goes; this one is a
	// button that signs somebody in, which is why it sits at the end rather than among them. ⚠ It is
	// drawn MONOCHROME like all eighteen of them - `icons.ts` carries no color literal anywhere and
	// `tests/theme.test.ts` is what keeps it that way, so the official four-color G would be the set's
	// first exception rather than a drawing choice. Google's own guidelines permit a monochrome mark;
	// if that ever has to become the colored asset for a brand review, it does NOT belong in this file.
	"brand-google"
] as const;

export type IconName = (typeof ICON_NAMES)[number];

export function isIconName(value: string): value is IconName {
	return (ICON_NAMES as readonly string[]).includes(value);
}
